import { cloneRgba, type Rgba } from '../color.ts';
import { signedArea } from '../geometry.ts';
import { hashSeed, mulberry32 } from '../rng.ts';
import {
  clampElevation,
  clampToBounds,
  clonePoint,
  closestPointOnSegment,
  distanceSquared,
} from './document.ts';
import { DEFAULT_LAND_COLOR } from './palette.ts';
import {
  clearEdgeBend,
  findBendHandleAt,
  findRingEdgeAt,
  moveBendHandle,
  moveRingVertex,
  removeRingVertex,
  setCornerRadius,
  setEdgeBend,
  tessellateRing,
} from './ring.ts';
import {
  DEFAULT_PROP_SCALE,
  SCATTER_ATTEMPTS_PER_PROP,
  SCATTER_INSET_PX,
  SCATTER_MIN_GAP_PX,
  SCATTER_AREA_PX2,
  clampSkirtM,
  emptyIslandLook,
  isPropId,
  propsInCategory,
} from './surfaces.ts';
import {
  DEFAULT_CORNER_RADIUS,
  DEFAULT_ISLAND_ELEVATION,
  MIN_RING_VERTICES,
  type Island,
  type IslandDecoration,
  type MapDocument,
  type PropId,
  type Vec2,
} from './types.ts';

export function pointInPolygon(point: Vec2, vertices: readonly Vec2[]): boolean {
  if (vertices.length < MIN_RING_VERTICES) {
    return false;
  }

  let inside = false;
  const lastIndex = vertices.length - 1;

  for (let i = 0, j = lastIndex; i < vertices.length; j = i, i += 1) {
    const current = vertices[i];
    const previous = vertices[j];

    if (!current || !previous) {
      continue;
    }

    const crosses =
      current.y > point.y !== previous.y > point.y &&
      point.x <
        ((previous.x - current.x) * (point.y - current.y)) / (previous.y - current.y) + current.x;

    if (crosses) {
      inside = !inside;
    }
  }

  return inside;
}

export function addIsland(map: MapDocument, vertices: readonly Vec2[]): Island {
  if (vertices.length < MIN_RING_VERTICES) {
    throw new Error(`An island needs at least ${MIN_RING_VERTICES} vertices`);
  }

  const island: Island = {
    id: `island-${map.nextIslandId}`,
    color: cloneRgba(DEFAULT_LAND_COLOR),
    elevation: DEFAULT_ISLAND_ELEVATION,
    ...emptyIslandLook(),
    vertices: vertices.map((vertex) => clampToBounds(clonePoint(vertex), map.bounds)),
    edges: vertices.map(() => ({ bend: null })),
    cornerRadius: DEFAULT_CORNER_RADIUS,
  };

  map.nextIslandId += 1;
  map.islands.push(island);
  return island;
}

export function findIsland(map: MapDocument, islandId: string): Island | undefined {
  return map.islands.find((island) => island.id === islandId);
}

export function findIslandAt(map: MapDocument, point: Vec2): Island | undefined {
  for (let i = map.islands.length - 1; i >= 0; i -= 1) {
    const island = map.islands[i];

    if (island && pointInPolygon(point, tessellateRing(island))) {
      return island;
    }
  }

  return undefined;
}

export function findVertexAt(
  map: MapDocument,
  point: Vec2,
  radius: number,
): { islandId: string; index: number } | null {
  const radiusSq = radius * radius;
  let best: { islandId: string; index: number; dist: number } | null = null;

  for (let i = map.islands.length - 1; i >= 0; i -= 1) {
    const island = map.islands[i];

    if (!island) {
      continue;
    }

    for (let index = 0; index < island.vertices.length; index += 1) {
      const vertex = island.vertices[index];

      if (!vertex) {
        continue;
      }

      const dist = distanceSquared(point, vertex);

      if (dist <= radiusSq && (!best || dist < best.dist)) {
        best = { islandId: island.id, index, dist };
      }
    }
  }

  return best ? { islandId: best.islandId, index: best.index } : null;
}

export function moveVertex(
  map: MapDocument,
  islandId: string,
  index: number,
  point: Vec2,
): boolean {
  const island = findIsland(map, islandId);

  if (!island) {
    return false;
  }

  return moveRingVertex(island, index, clampToBounds(point, map.bounds));
}

export function removeVertex(map: MapDocument, islandId: string, index: number): boolean {
  const island = findIsland(map, islandId);

  if (!island || island.vertices[index] === undefined) {
    return false;
  }

  if (island.vertices.length <= MIN_RING_VERTICES) {
    return false;
  }

  return removeRingVertex(island, index);
}

export function removeIsland(map: MapDocument, islandId: string): boolean {
  const index = map.islands.findIndex((island) => island.id === islandId);

  if (index < 0) {
    return false;
  }

  map.islands.splice(index, 1);
  return true;
}

export function setIslandCornerRadius(map: MapDocument, islandId: string, radius: number): boolean {
  const island = findIsland(map, islandId);

  if (!island) {
    return false;
  }

  setCornerRadius(island, radius);
  return true;
}

export function setIslandColor(map: MapDocument, islandId: string, color: Rgba): boolean {
  const island = findIsland(map, islandId);

  if (!island) {
    return false;
  }

  island.color = cloneRgba(color);
  return true;
}

export function setIslandElevation(map: MapDocument, islandId: string, elevation: number): boolean {
  const island = findIsland(map, islandId);

  if (!island) {
    return false;
  }

  island.elevation = clampElevation(elevation);
  return true;
}

export function setIslandSkirtM(map: MapDocument, islandId: string, skirtM: number): boolean {
  const island = findIsland(map, islandId);

  if (!island) {
    return false;
  }

  island.skirtM = clampSkirtM(skirtM);
  return true;
}

export function plantIslandProp(
  map: MapDocument,
  islandId: string,
  catalog: PropId,
  point: Vec2,
): IslandDecoration | null {
  const island = findIsland(map, islandId);

  if (!island || !isPropId(catalog)) {
    return null;
  }

  const outline = tessellateRing(island);

  if (!pointInPolygon(point, outline)) {
    return null;
  }

  const decoration: IslandDecoration = {
    id: `deco-${island.nextDecorationId}`,
    catalog,
    x: clampToBounds(clonePoint(point), map.bounds).x,
    y: clampToBounds(clonePoint(point), map.bounds).y,
    scale: DEFAULT_PROP_SCALE,
    rotation: 0,
  };
  island.nextDecorationId += 1;
  island.decorations.push(decoration);
  return decoration;
}

export function removeIslandDecoration(
  map: MapDocument,
  islandId: string,
  decorationId: string,
): boolean {
  const island = findIsland(map, islandId);

  if (!island) {
    return false;
  }

  const index = island.decorations.findIndex((decoration) => decoration.id === decorationId);

  if (index < 0) {
    return false;
  }

  island.decorations.splice(index, 1);
  return true;
}

export function clearIslandDecorations(map: MapDocument, islandId: string): boolean {
  const island = findIsland(map, islandId);

  if (!island) {
    return false;
  }

  island.decorations = [];
  return true;
}

export function scatterIsland(
  map: MapDocument,
  islandId: string,
  props?: readonly PropId[],
): boolean {
  const island = findIsland(map, islandId);

  if (!island) {
    return false;
  }

  const catalog =
    props && props.length > 0
      ? props
      : propsInCategory('vegetation').map((entry) => entry.id);
  scatterIslandDecorations(island, map.environment.seed, catalog);
  return true;
}

export function scatterIslandDecorations(
  island: Island,
  mapSeed: number,
  props: readonly PropId[],
): void {
  island.decorations = [];
  island.nextDecorationId = 1;

  if (props.length === 0) {
    return;
  }

  const outline = tessellateRing(island);

  if (outline.length < 3) {
    return;
  }

  let minX = outline[0]!.x;
  let minY = outline[0]!.y;
  let maxX = minX;
  let maxY = minY;

  for (let i = 1; i < outline.length; i += 1) {
    const point = outline[i];

    if (!point) {
      continue;
    }

    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }

  const area = Math.abs(signedArea(outline));
  const cell = SCATTER_AREA_PX2;
  const target = Math.max(1, Math.round(area / cell));
  const rng = mulberry32(hashSeed(mapSeed, island.id, 'scatter'));
  const width = maxX - minX;
  const height = maxY - minY;
  const gapSq = SCATTER_MIN_GAP_PX * SCATTER_MIN_GAP_PX;

  for (let n = 0; n < target; n += 1) {
    let placed: Vec2 | null = null;

    for (let attempt = 0; attempt < SCATTER_ATTEMPTS_PER_PROP; attempt += 1) {
      const candidate = {
        x: minX + rng() * width,
        y: minY + rng() * height,
      };

      if (!pointInPolygon(candidate, outline)) {
        continue;
      }

      if (minEdgeDistance(candidate, outline) < SCATTER_INSET_PX) {
        continue;
      }

      const crowded = island.decorations.some(
        (decoration) => distanceSquared(candidate, decoration) <= gapSq,
      );

      if (crowded) {
        continue;
      }

      placed = candidate;
      break;
    }

    if (!placed) {
      continue;
    }

    const catalog = props[Math.floor(rng() * props.length)] ?? props[0]!;
    island.decorations.push({
      id: `deco-${island.nextDecorationId}`,
      catalog,
      x: placed.x,
      y: placed.y,
      scale: DEFAULT_PROP_SCALE * (0.85 + rng() * 0.3),
      rotation: rng() * Math.PI * 2,
    });
    island.nextDecorationId += 1;
  }
}

function minEdgeDistance(point: Vec2, outline: readonly Vec2[]): number {
  if (outline.length < 2) {
    return 0;
  }

  let best = Infinity;

  for (let i = 0; i < outline.length; i += 1) {
    const a = outline[i];
    const b = outline[(i + 1) % outline.length];

    if (!a || !b) {
      continue;
    }

    const nearest = closestPointOnSegment(point, a, b);
    best = Math.min(best, distanceSquared(point, nearest));
  }

  return Math.sqrt(best);
}

export function findIslandEdgeAt(
  map: MapDocument,
  islandId: string,
  point: Vec2,
  radius: number,
): number | null {
  const island = findIsland(map, islandId);
  return island ? findRingEdgeAt(island, point, radius) : null;
}

export function findIslandBendAt(
  map: MapDocument,
  islandId: string,
  point: Vec2,
  radius: number,
): number | null {
  const island = findIsland(map, islandId);
  return island ? findBendHandleAt(island, point, radius) : null;
}

export function setIslandEdgeBend(
  map: MapDocument,
  islandId: string,
  edgeIndex: number,
  point: Vec2,
): boolean {
  const island = findIsland(map, islandId);
  return island ? setEdgeBend(island, edgeIndex, clampToBounds(point, map.bounds)) : false;
}

export function moveIslandBend(
  map: MapDocument,
  islandId: string,
  edgeIndex: number,
  point: Vec2,
): boolean {
  const island = findIsland(map, islandId);
  return island ? moveBendHandle(island, edgeIndex, clampToBounds(point, map.bounds)) : false;
}

export function clearIslandEdgeBend(map: MapDocument, islandId: string, edgeIndex: number): boolean {
  const island = findIsland(map, islandId);
  return island ? clearEdgeBend(island, edgeIndex) : false;
}
